import { useState } from "react";
import { motion } from "framer-motion";
import { SpotifyLogo, Waveform, MusicNotes } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { API } from "../App";

export const LoginPage = () => {
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/login`);
      if (response.ok) {
        const data = await response.json();
        if (data.auth_url) {
          window.location.href = data.auth_url;
        }
      } else {
        const errorData = await response.json();
        console.error("Login failed:", errorData);
        setLoading(false);
      }
    } catch (error) {
      console.error("Login error:", error);
      setLoading(false);
    }
  };

  return (
    <div className="login-container" data-testid="login-page">
      {/* Background */}
      <div 
        className="login-bg"
        style={{
          backgroundImage: `url(https://images.pexels.com/photos/28583190/pexels-photo-28583190.jpeg)`
        }}
      />
      <div className="login-overlay" />

      {/* Content */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 text-center px-6 max-w-lg"
      >
        {/* Logo/Icon */}
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-8 flex justify-center"
        >
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#FF3366] to-[#FF6B6B] flex items-center justify-center pulse-glow">
            <Waveform size={40} weight="bold" className="text-white" />
          </div>
        </motion.div>

        {/* Title */}
        <motion.h1 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4"
          style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
        >
          <span className="gradient-text">Pandora</span>{" "}
          <span className="text-white">Vibes</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-[#A1A1AA] text-base sm:text-lg mb-8 leading-relaxed"
        >
          Odkryj nowych artystów dopasowanych do Twojego gustu muzycznego. 
          Analiza AI w stylu Pandora Music Genome Project.
        </motion.p>

        {/* Features */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="flex flex-wrap justify-center gap-4 mb-10"
        >
          {[
            "Analiza playlist",
            "Top artyści",
            "AI rekomendacje"
          ].map((feature, i) => (
            <span 
              key={i}
              className="px-4 py-2 rounded-full bg-[#1A1A1A] border border-[#27272A] text-sm text-[#FAFAFA]"
            >
              {feature}
            </span>
          ))}
        </motion.div>

        {/* Login Button */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Button
            data-testid="spotify-login-btn"
            onClick={handleLogin}
            disabled={loading}
            className="bg-[#FF3366] hover:bg-[#FF4477] text-white px-8 py-6 text-lg font-semibold rounded-full transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
          >
            {loading ? (
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Łączenie...</span>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <SpotifyLogo size={24} weight="fill" />
                <span>Połącz ze Spotify</span>
              </div>
            )}
          </Button>
        </motion.div>

        {/* Footer note */}
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-8 text-xs text-[#71717A]"
        >
          Wymagane konto Spotify. Nie przechowujemy Twoich danych logowania.
        </motion.p>
      </motion.div>

      {/* Decorative elements */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-10 left-10 hidden lg:block"
      >
        <MusicNotes size={120} weight="thin" className="text-[#FF3366]" />
      </motion.div>
    </div>
  );
};
